import { subYears, addYears } from "date-fns"
import { useMemo, useState } from "react"
import { useForecastGraph } from "~/hooks/useForecastGraph"
import useInventoryGraph, { INBOUND_STACK } from "~/hooks/useInventoryGraph"
import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"
import { getCollectionPlotBands } from "~/components/reusable/graphs/utils/GraphPlotLineUtils"
import SuperChart, { type DateChartSeries } from "~/components/reusable/graphs/SuperChart"
import { ChartType, LegendAlignment, TodayPlotLine, DateType } from "~/constants/graphs"
import { api } from "~/utils/api"
import { startOfTodayUTC } from "~/utils/datetime"
import { useVariantsByIds } from "~/hooks/useVariants"
import AreaGraphSilhouette from "~/components/reusable/skeletonSilhouette/AreaGraphSilhouette"
import { type InventoryMetric } from "~/utils/graphUtils"
import { useCompany } from "~/hooks/useCompany"

type Props = {
  variantIds: string[]
  warehouseId?: string
  channel?: string
  graphHeight?: number
  startDate?: Date
  endDate?: Date
  isMonthly?: boolean
  title?: string
  defaultShownSeries?: string[]
  metric?: InventoryMetric
}

export const InventoryGraphCard = ({
  variantIds,
  warehouseId,
  channel,
  graphHeight = 280,
  startDate: startDateProp,
  endDate: endDateProp,
  isMonthly = false,
  title = "Inventory",
  defaultShownSeries,
  metric = "Units",
}: Props) => {
  const [defaultRange] = useState(() => ({
    start: subYears(startOfTodayUTC(), 1),
    end: addYears(startOfTodayUTC(), 1),
  }))
  const startDate = startDateProp ?? defaultRange.start
  const endDate = endDateProp ?? defaultRange.end
  const { data: company } = useCompany()
  const resolvedTitle =
    metric === "Cost" && company?.currency ? `${title} (${company.currency})` : title
  const variants = useVariantsByIds(variantIds)
  const hasNoosVariants = useMemo(() => {
    return variants.some((v) => v.productType === "noos")
  }, [variants])

  const { salesGraphData, adjustedForecastGraphData } = useForecastGraph({
    variantIds,
    warehouseId,
    monthly: isMonthly,
    startDate,
    endDate,
    channel,
  })

  const {
    inventoryPastGraphData,
    inventoryFutureGraphData,
    safetyStockGraphData,
    optimalProjectedStockGraphData,
    suggestedIncomingStockGraphData,
    incomingStockGraphData,
    expectedReturnsGraphData,
    xPlotLinesInventory,
    isLoading,
  } = useInventoryGraph({
    variantIds,
    warehouseId,
    channel,
    monthly: isMonthly,
    startDate,
    endDate,
    metric,
  })

  const series = [
    inventoryPastGraphData,
    inventoryFutureGraphData,
    safetyStockGraphData,
    incomingStockGraphData,
    // Sales and demand overlay the stock series, which own the area fills here — thin lines
    // rather than fills, and opt-in from the legend.
    ...[salesGraphData, adjustedForecastGraphData].map((x) => ({
      ...x,
      type: "spline" as const,
      lineWidth: 2,
      disabled: true,
    })),
  ].filter((x): x is DateChartSeries => !!x?.data)

  if (hasNoosVariants) {
    series.push(
      ...[optimalProjectedStockGraphData, suggestedIncomingStockGraphData].filter(
        (x): x is DateChartSeries => !!x?.data,
      ),
    )
  }

  if (expectedReturnsGraphData) {
    series.push(expectedReturnsGraphData)
  }

  const { data: collections } = api.collection.getCollectionsForVariants.useQuery(
    { variantIds: [variantIds[0]!] },
    { enabled: variantIds.length === 1 },
  )

  const collectionPlotBands = useMemo(() => {
    return getCollectionPlotBands(collections ?? [])
  }, [collections])

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent style={{ height: graphHeight }}>
          <AreaGraphSilhouette />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{resolvedTitle}</CardTitle>
      </CardHeader>
      <CardContent>
        <SuperChart
          type={ChartType.TimeSeries}
          data={series.map((x) => ({
            ...x,
            disabled: defaultShownSeries
              ? !defaultShownSeries.includes(x.name)
              : (x.disabled ?? false),
          }))}
          compact={graphHeight < 200}
          height={graphHeight}
          plotBands={{
            x: collectionPlotBands,
          }}
          dateRange={{ start: startDate, end: endDate }}
          dateType={isMonthly ? DateType.Monthly : DateType.Weekly}
          todayPlotLine={TodayPlotLine.Detailed}
          legendAlignment={LegendAlignment.Bottom}
          stackSectionLabels={{ [INBOUND_STACK.stack]: "Expected supply" }}
          unsectionedLabel="Stock level"
          plotLines={{
            x: xPlotLinesInventory,
          }}
        />
      </CardContent>
    </Card>
  )
}
